/* eslint-disable max-lines-per-function */
import { motion } from "framer-motion";
import { Eye, EyeOff, X } from "lucide-react";
import type { ReactElement } from "react";
import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "../../auth/AuthContext";
import { LoadingDots } from "../../shared/components/Loading";
import { Button } from "../../shared/components/ui/button";
import { Input } from "../../shared/components/ui/input";
import { Label } from "../../shared/components/ui/label";
import { useToast } from "../../shared/hooks/use-toast";
import { useTransitionNavigation } from "../../shared/hooks/useTransitionNavigation";
import { pageTransitions } from "../../shared/transitions/pageTransitions";

/**
 * Custom event dispatched when sign-in succeeds
 * Used by RootLayout to trigger blur card fade animation
 */
const SIGNIN_SUCCESS_EVENT = "signin-success";

/**
 * Validate email format
 * Uses a ReDoS-safe regex pattern with bounded quantifiers to prevent catastrophic backtracking.
 * Pattern allows standard email formats while rejecting malicious inputs.
 */
function isValidEmail(email: string): boolean {
  // ReDoS-safe email validation - based on OWASP recommendations
  // Limits length and uses possessive-like matching to prevent backtracking issues
  const emailRegex =
    /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

  // Additional length check to prevent DoS via extremely long inputs
  if (email.length > 254) return false;

  return emailRegex.test(email);
}

export function SignInPage(): ReactElement {
  const { signIn, loading: authLoading } = useAuth();
  const location = useLocation();
  const { toast } = useToast();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showEmailError, setShowEmailError] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Use transition navigation for smooth SignIn → destination transition
  const { isTransitioning, transitionTo } = useTransitionNavigation();

  // Stable navigation handler (prevent event handler orphaning during View Transitions)
  const handleNavigateSignUp = useCallback(() => {
    transitionTo("/sign-up", pageTransitions.slideHorizontal);
  }, [transitionTo]);

  const isEmailValid = email ? isValidEmail(email) : true; // Don't show error on empty

  // Check for password reset success
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("reset") === "success") {
      setSuccessMessage("Password reset successful! You can now sign in with your new password.");
    }
  }, [location.search]);

  // Unlock any existing orientation lock from previous pages
  // Runs once on mount to ensure clean orientation state
  useEffect(() => {
    const screenOrientation = screen?.orientation as { unlock?: () => void } | undefined;
    if (screenOrientation?.unlock) {
      screenOrientation.unlock();
    }
  }, []);

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    // Manually validate required fields to show custom toast instead of browser validation
    if (!email.trim()) {
      toast({
        description: "Please enter your email address",
        variant: "destructive",
      });
      return;
    }
    if (!password?.trim()) {
      toast({
        description: "Please enter your password",
        variant: "destructive",
      });
      return;
    }
    void performAuth();
  };

  const performAuth = async (): Promise<void> => {
    // Validation (already checked in handleSubmit, but double-check)
    if (!isValidEmail(email.trim())) {
      toast({
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);

    try {
      await signIn({ email: email.trim(), password });
      // Trigger blur card fade before navigation
      globalThis.dispatchEvent(new CustomEvent(SIGNIN_SUCCESS_EVENT));

      // Redirect to the page user was trying to access, or /home by default
      const from = (location.state as { from?: { pathname: string } })?.from?.pathname || "/home";
      transitionTo(from, pageTransitions.scaleDown);
    } catch (err) {
      console.error("Auth failed:", err);

      // Map API error codes to user-friendly messages
      let errorMessage = "Unable to sign in. Please try again.";

      if (err instanceof Error) {
        const message = err.message.toLowerCase();
        if (message.includes("invalid_credentials") || message.includes("401")) {
          errorMessage = "Incorrect email or password. Please try again.";
        } else if (message.includes("429") || message.includes("rate limit") || message.includes("too many")) {
          errorMessage = "Too many attempts. Please wait a moment and try again.";
        } else if (message.includes("network") || message.includes("fetch")) {
          errorMessage = "Network error. Please check your connection.";
        } else if (message.includes("500")) {
          errorMessage = "Server error. Please try again later.";
        }
      }

      toast({
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const isLoading = loading || authLoading;

  return (
    <motion.div
      className="relative min-h-screen flex items-center justify-center"
      initial="initial"
      animate={isTransitioning ? "exit" : "animate"}
      exit="exit"
      variants={pageTransitions.scaleDown}
    >
      {/* Draggable titlebar region for Electron (macOS only) */}
      <div
        className="absolute top-0 left-0 right-0 h-[52px] z-50 electron-drag-region"
        style={{ WebkitAppRegion: "drag" } as React.CSSProperties}
      />

      {/* Mapbox satellite background now renders in RootLayout - shows through this page */}
      {/* Blur card is also in RootLayout to share stacking context with map */}

      {/* Content layer with card - dimensions synced via SIGNIN_CARD constant */}
      <div className={`relative z-10 mx-auto w-[400px]`}>
        {/* Card background - semi-transparent with border */}
        <div className="absolute inset-0 bg-black/80 rounded-lg border border-neutral-800/60" />

        {/* Card content */}
        <div className="relative p-6">
          <h1 className="text-xl font-semibold mb-6 text-white text-center">Sign in to Lumi</h1>

          <form onSubmit={handleSubmit} className="grid gap-4">
            {successMessage && (
              <div
                role="alert"
                aria-live="polite"
                className="text-green-400 text-sm bg-green-900/20 border border-green-800 rounded-md px-3 py-2"
              >
                {successMessage}
              </div>
            )}

            <div className="grid gap-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setShowEmailError(false);
                }}
                onBlur={() => setShowEmailError(true)}
                disabled={isLoading}
                autoComplete="username"
                autoFocus
                className={showEmailError && !isEmailValid ? "border-red-400" : ""}
              />
              {showEmailError && !isEmailValid && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.2, ease: "easeOut" }}
                  className="flex items-center gap-2 text-xs text-red-400 overflow-hidden"
                >
                  <X size={12} />
                  <span>Please enter a valid email address</span>
                </motion.div>
              )}
            </div>

            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <Link
                  to="/forgot-password"
                  tabIndex={-1}
                  className="text-xs text-lumi-accent hover:underline transition-all"
                >
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isLoading}
                  autoComplete="current-password"
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-200 transition-colors"
                  disabled={isLoading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button type="submit" disabled={isLoading} variant="outline" className="mt-2">
              {isLoading ? (
                <span className="flex items-center">
                  Signing in
                  <LoadingDots className="ml-1" />
                </span>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          <div className="mt-4 text-center">
            <Button
              type="button"
              variant="ghost-sm"
              onClick={handleNavigateSignUp}
              disabled={isLoading}
              className="text-sm text-lumi-accent hover:text-lumi-accent-80 underline"
            >
              Need an account? Sign up
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
